import { CircleCheck, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { useFetcher, useSearchParams } from 'react-router'
import { assets } from '#app/data/assets'
import { IncomeReassuranceModal } from './IncomeReassuranceModal'
import {
	ScreeningSectionCard,
	ScreeningStepContainer,
} from './ScreeningStepContainer'

interface AccountSummary {
	institution?: string
	accountTitle?: string
	accountType?: string
	maskedAccountNumber?: string
	holderName?: string
	accountCount?: number
}

interface AccountEntry {
	flinksLoginId?: string
	flinksRecordId?: string
	status?: string
	addedAt?: string
	isPrimary?: boolean
	summary?: AccountSummary | null
}

interface VerifyIncomeStepProps {
	currentStep?: number
	onNext?: () => void
	onBack?: () => void
	userId?: string
	applicationId?: string | null
	stepData?: Record<string, unknown>
	isEditMode?: boolean
	isStepCompleted?: boolean
	incomeReassuranceCallTriggered?: boolean
	applicationType?: 'primary' | 'co_applicant' | 'guarantor'
}

type FlinksConnectData =
	| {
			success: true
			flinksConnectUrl: string
			token: string
			redirectUrl: string
	  }
	| {
			error: string
	  }
	| null

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const partnerLogos = [
	...assets.logos.flinksPartnerLogos,
	...assets.logos.flinksPartnerLogos,
]

export function LogoMarquee({ className }: { className?: string }) {
	return (
		<section className={`w-full overflow-hidden py-[20px] ${className ?? ''}`}>
			<Marquee
				direction="left"
				speed={40}
				gradient
				gradientColor="#ffffff"
				gradientWidth={150}
				autoFill={true}
			>
				<div className="flex w-full px-4">
					{partnerLogos.map((logo, i) => (
						<img
							key={i}
							src={logo}
							alt="partner logo"
							className="mx-5 h-[30px] w-auto opacity-70 md:mx-14 md:h-[47px]"
							onError={(e) => {
								e.currentTarget.style.display = 'none'
							}}
						/>
					))}
				</div>
			</Marquee>
		</section>
	)
}

function ErrorBanner({ message }: { message: string }) {
	return (
		<div className="rounded-lg border border-red-200 bg-red-50 p-4">
			<p className="text-[16px] text-red-600">{message}</p>
		</div>
	)
}

function WarningBanner({ message }: { message: string }) {
	return (
		<div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
			<p className="text-[16px] text-amber-700">{message}</p>
		</div>
	)
}

function LinkedAccountCard({ account }: { account: AccountEntry }) {
	const summary = account.summary
	const isSuccess = account.status === 'success'
	const isPending = account.status === 'pending'

	return (
		<div className="flex items-center gap-4">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E8E4DF] bg-white">
				{isSuccess ? (
					<CircleCheck className="h-5 w-5 text-green-600" />
				) : isPending ? (
					<Loader2 className="h-5 w-5 animate-spin text-[#484848]" />
				) : (
					<div className="h-2 w-2 rounded-full bg-gray-400" />
				)}
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-[15px] font-medium text-[#32302F]">
					{summary?.institution || summary?.accountTitle || 'Bank Account'}
				</p>
				<div className="flex flex-wrap items-center gap-x-2 text-[13px] text-[#747474]">
					{summary?.accountType && <span>{summary.accountType}</span>}
					{summary?.accountType && summary?.maskedAccountNumber && (
						<span className="text-[#CCCCCC]">&middot;</span>
					)}
					{summary?.maskedAccountNumber && (
						<span className="font-mono">{summary.maskedAccountNumber}</span>
					)}
				</div>
				{summary?.holderName && (
					<p className="text-[13px] text-[#747474]">{summary.holderName}</p>
				)}
				{!summary && isPending && (
					<p className="text-[13px] text-[#747474]">Processing...</p>
				)}
			</div>
			<div className="shrink-0">
				{isSuccess && (
					<span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[12px] font-medium text-green-700">
						Connected
					</span>
				)}
				{isPending && (
					<span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[12px] font-medium text-amber-700">
						Syncing
					</span>
				)}
			</div>
		</div>
	)
}

function LinkedAccountsList({ accounts }: { accounts: AccountEntry[] }) {
	if (accounts.length === 0) return null

	return (
		<ScreeningSectionCard title="Connected Accounts">
			<div className="space-y-3 -mt-3">
				{accounts.map((account, i) => (
					<LinkedAccountCard
						key={account.flinksLoginId || i}
						account={account}
					/>
				))}
			</div>
		</ScreeningSectionCard>
	)
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function VerifyIncomeStep({
	currentStep: _currentStep = 5,
	onNext,
	onBack,
	userId: _userId,
	applicationId,
	stepData: _stepData,
	isEditMode = false,
	isStepCompleted = false,
	incomeReassuranceCallTriggered = false,
	applicationType = 'primary',
}: VerifyIncomeStepProps) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const fetcher = useFetcher<FlinksConnectData>()
	const [searchParams, setSearchParams] = useSearchParams()
	const [showFlinksIframe, setShowFlinksIframe] = useState(false)

	// Parse accounts array from step data.
	// For old applications without an accounts array, synthesize one from the legacy flat fields.
	// Also handles the mixed state where accounts array exists but the legacy account isn't in it.
	const additionalRequested = Boolean(_stepData?.additionalAccountRequested)
	let accounts: AccountEntry[] = (_stepData?.accounts as AccountEntry[]) || []
	const legacyLoginId = (_stepData?.flinksLoginId as string) || undefined
	const legacyInArray =
		legacyLoginId && accounts.some((a) => a.flinksLoginId === legacyLoginId)

	if (
		!legacyInArray &&
		_stepData?.flinksRecordId &&
		typeof _stepData.flinksRecordId === 'string' &&
		legacyLoginId
	) {
		accounts = [
			{
				flinksLoginId: legacyLoginId,
				status: 'success',
				addedAt: undefined,
				isPrimary: true,
				summary: null,
			},
			...accounts,
		]
	}
	const linkedAccounts = accounts.filter((a) => a.status === 'success')
	const hasExistingAccounts = linkedAccounts.length > 0

	// The step is optimistically marked completed at the Flinks redirect; the
	// background fetch can fail terminally afterwards. When EVERY linked account
	// has terminally failed (failed status, no processed record — mirrors
	// getFlinksReadiness), the stale `completed` flag must not show success:
	// the applicant needs to reconnect. Partial failures (≥1 success) still pass.
	const allAccountsFailed =
		accounts.length > 0 &&
		accounts.every((a) => a.status === 'failed' && !a.flinksRecordId)

	// When additionalAccountRequested is true, treat the step as not yet complete
	// so the tenant sees the "Connect Another Bank" flow.
	const flinksCompleted = Boolean(
		_stepData &&
			(_stepData.completed === true || _stepData.completed === 'true') &&
			!additionalRequested &&
			!allAccountsFailed,
	)

	// Reassurance modal: shown once per session when a Retell call was placed
	// and the applicant hasn't yet connected a bank. Dismissal is remembered in
	// sessionStorage so it doesn't reappear on re-render or navigation.
	const reassuranceDismissKey = `tt_income_reassurance_dismissed_${applicationId ?? 'unknown'}`
	const [showReassurance, setShowReassurance] = useState(() => {
		if (typeof window === 'undefined') return false
		if (!incomeReassuranceCallTriggered) return false
		if (hasExistingAccounts || flinksCompleted) return false
		return sessionStorage.getItem(reassuranceDismissKey) !== '1'
	})

	const handleDismissReassurance = (open: boolean) => {
		if (!open) {
			try {
				sessionStorage.setItem(reassuranceDismissKey, '1')
			} catch {
				/* sessionStorage unavailable — non-fatal */
			}
		}
		setShowReassurance(open)
	}

	const flinksSuccess = searchParams.get('flinks_success') === 'true'
	const flinksError = searchParams.get('error')
	const duplicateAccount = searchParams.get('duplicate_account') === 'true'
	const showSuccess = flinksCompleted && !flinksSuccess

	const flinksData =
		fetcher.data && 'success' in fetcher.data && fetcher.data.success
			? fetcher.data
			: null

	const fetcherError =
		fetcher.data && 'error' in fetcher.data ? String(fetcher.data.error) : null

	const isLoading = isSubmitting || (showFlinksIframe && !flinksData)
	const hasError = !isLoading && Boolean(flinksError || fetcherError)
	const errorMessage = flinksError || fetcherError || 'Unknown error'

	// First-time connect prompt (no accounts yet). In this state the step's
	// primary "Next" button drives the bank connection ("Verify Income").
	const showConnectPrompt =
		!isLoading && !hasError && !showSuccess && !hasExistingAccounts

	const handleConnectBank = () => {
		if (!applicationId) {
			console.error('[VerifyIncomeStep] No applicationId available')
			return
		}

		setShowFlinksIframe(true)
		const formData = new FormData()
		formData.append('_action', 'verify-income-init')
		formData.append('applicationId', applicationId)
		if (_userId) {
			formData.append('userId', _userId)
		}
		void fetcher.submit(formData, { method: 'POST' })
	}

	const handleRetry = () => {
		searchParams.delete('error')
		setSearchParams(searchParams, { replace: true })
		setShowFlinksIframe(false)
		const formData = new FormData()
		formData.append('_action', 'verify-income-init')
		if (applicationId) {
			formData.append('applicationId', applicationId)
		}
		if (_userId) {
			formData.append('userId', _userId)
		}
		void fetcher.submit(formData, { method: 'POST' })
	}

	const handleCloseIframe = () => {
		setShowFlinksIframe(false)
	}

	const handleNext = () => {
		if (showSuccess && onNext) {
			onNext()
		}
	}

	// Clear flinks_success URL param after redirect
	useEffect(() => {
		if (flinksSuccess) {
			searchParams.delete('flinks_success')
			setSearchParams(searchParams, { replace: true })
		}
	}, [flinksSuccess, searchParams, setSearchParams])

	// Clear duplicate_account URL param after it's been shown
	useEffect(() => {
		if (duplicateAccount) {
			const timer = setTimeout(() => {
				searchParams.delete('duplicate_account')
				setSearchParams(searchParams, { replace: true })
			}, 8000)
			return () => clearTimeout(timer)
		}
	}, [duplicateAccount, searchParams, setSearchParams])

	// Track fetcher submission state
	useEffect(() => {
		if (fetcher.state === 'submitting') {
			setIsSubmitting(true)
		} else if (fetcher.state === 'idle') {
			setIsSubmitting(false)
		}
	}, [fetcher.state])

	// Manage body class for full-screen iframe mode
	useEffect(() => {
		const isIframeOpen = showFlinksIframe && flinksData && !flinksCompleted

		if (isIframeOpen) {
			document.body.classList.add('flinks-iframe-open')
		} else {
			document.body.classList.remove('flinks-iframe-open')
		}

		return () => {
			document.body.classList.remove('flinks-iframe-open')
		}
	}, [showFlinksIframe, flinksData, flinksCompleted])

	// ── Full-screen iframe overlay (completely different layout) ──────────
	if (showFlinksIframe && flinksData && !flinksCompleted) {
		const isStaging =
			(typeof globalThis.window === 'undefined'
				? process.env.NODE_ENV
				: globalThis.ENV?.MODE) !== 'production'

		const iframeSrc = isStaging
			? `${flinksData.flinksConnectUrl}&redirectUrl=${encodeURIComponent(flinksData.redirectUrl)}&authorizeToken=${flinksData.token}`
			: `${flinksData.flinksConnectUrl}/?redirectUrl=${encodeURIComponent(flinksData.redirectUrl)}&authorizeToken=${flinksData.token}&daysOfTransactions=Days365`

		return (
			<div className="fixed inset-0 z-[100] min-h-screen w-full bg-white">
				<button
					type="button"
					onClick={handleCloseIframe}
					className="cursor-pointer absolute top-4 right-4 z-[101] flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white shadow-md transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-[#32302F] focus:ring-offset-2 focus:outline-none"
					aria-label="Close bank connection"
				>
					<X className="h-5 w-5 text-[#32302F]" />
				</button>

				<LogoMarquee className="bg-white" />

				<div className="w-full" style={{ height: 'calc(100vh - 87px)' }}>
					<iframe
						title="Flinks Connect Window"
						src={iframeSrc}
						style={{ width: '100%', height: '100%', border: 'none' }}
						allow="fullscreen"
					/>
				</div>
			</div>
		)
	}

	// ── Single container for all non-iframe states ───────────────────────
	return (
		<ScreeningStepContainer
			title="Verify Income"
			onNext={
				showSuccess
					? handleNext
					: showConnectPrompt
						? handleConnectBank
						: undefined
			}
			onBack={onBack}
			isSubmitting={isSubmitting || fetcher.state === 'submitting'}
			canGoNext={showSuccess || showConnectPrompt}
			isEditMode={isEditMode}
			isStepCompleted={isStepCompleted}
			nextButtonText={
				showConnectPrompt
					? 'Verify Income'
					: isEditMode && showSuccess
						? 'Confirm'
						: undefined
			}
			des="You will be redirected to Flinks' Secure Portal"
		>
			<IncomeReassuranceModal
				open={showReassurance}
				onOpenChange={handleDismissReassurance}
				isGuarantor={applicationType === 'guarantor'}
			/>
			<div className="space-y-4 sm:space-y-6">
				{!isLoading && hasExistingAccounts && (
					<div>
						<LinkedAccountsList accounts={linkedAccounts} />
						{!hasError && !showSuccess && (
							<div className="mt-3 flex justify-end">
								<button
									type="button"
									onClick={handleConnectBank}
									disabled={
										isSubmitting ||
										fetcher.state === 'submitting' ||
										showFlinksIframe ||
										flinksCompleted
									}
									className="cursor-pointer rounded-[30px] bg-[#32302F] px-8 py-3 text-[16px] font-medium text-white transition-colors hover:bg-[#1a1918] disabled:cursor-not-allowed disabled:opacity-50"
								>
									{fetcher.state === 'submitting'
										? 'Connecting...'
										: '+ Add Account'}
								</button>
							</div>
						)}
					</div>
				)}

				{duplicateAccount && (
					<WarningBanner message="This bank account is already connected. Please connect a different bank account." />
				)}

				{/* Loading state */}
				{isLoading && (
					<ScreeningSectionCard title="">
						<div className="space-y-6">
							<div className="text-center">
								<p className="mb-4 text-[16px] text-[#484848]">
									Connecting to your bank...
								</p>
							</div>
							<LogoMarquee />
						</div>
					</ScreeningSectionCard>
				)}

				{/* Error state */}
				{hasError && (
					<ScreeningSectionCard title="Income Verification">
						<ErrorBanner message={errorMessage} />
						<button
							type="button"
							onClick={handleRetry}
							className="cursor-pointer mt-4 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700"
						>
							Try Again
						</button>
					</ScreeningSectionCard>
				)}

				{/* Success state */}
				{showSuccess && (
					<ScreeningSectionCard>
						<p className="flex items-center gap-2 text-[16px] text-green-600">
							<CircleCheck className="h-4 w-4 shrink-0" />
							{linkedAccounts.length > 1
								? `${linkedAccounts.length} bank accounts connected successfully. You can proceed to the next step.`
								: 'Your information has been collected successfully. You can proceed to the next step.'}
						</p>
					</ScreeningSectionCard>
				)}

				{/* Default: connect bank (first time only — returning users use the "+ Add Account" button above) */}
				{showConnectPrompt && (
					<ScreeningSectionCard>
						<div className="space-y-4">
							{allAccountsFailed && (
								<ErrorBanner message="We couldn't complete your bank connection — the verification didn't go through on the bank's side. Please reconnect your bank account to continue." />
							)}
							<div className="flex items-start">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p className="text-[16px] text-[#32302F]">
									<span className="font-[500]">
										BANKING PROFILE MUST BELONG TO YOU
									</span>{' '}
									( Do not complete this process on behalf of someone else).
								</p>
							</div>
							<div className="flex items-start">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p className="text-[16px] text-[#32302F]">
									Instant Verification
								</p>
							</div>
							<div className="flex items-start">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p className="text-[16px] text-[#32302F]">
									Bank-Level Security
								</p>
							</div>
						</div>
					</ScreeningSectionCard>
				)}
			</div>
		</ScreeningStepContainer>
	)
}
